import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import AppBar from "@mui/material/AppBar";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Popper from "@mui/material/Popper";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { makeStyles } from "tss-react/mui";
import ClearIcon from "@mui/icons-material/Clear";

import CommonLayout from "../Common/Layout";
import DataTable from "./DataTable";
import ComponentAvatar from "./ComponentAvatar";
import { ButtonUI } from "../Common";
import { setPrevState } from "../../util/common";
import BusinessRulesService from "../../service/businessRulesService";
import { DATA_SOURCE } from "../../util/Data/DataModeling";

const useStyles = makeStyles()((theme) => ({
  AutoComplete: {
    "& .MuiAutocomplete-inputRoot": {
      flexWrap: "nowrap !important",
    },
    alignSelf: "flex-end",
  },
  option: {
    "&.Mui-focused ": {
      backgroundColor: "rgba(0, 0, 0, 0.02) !important",
    },
  },
  SelectChip: {
    maxWidth: "90px",
    marginRight: "2px",
  },
  Header: {
    fontSize: "1.1rem",
    minHeight: "60px !important",
    backgroundColor: "#e8edf1",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
    "& .MuiButton-root.Mui-disabled": {
      backgroundColor: "rgba(0, 0, 0, 0.12) !important",
    },
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    padding: "4 0 0 0",
  },
}));

const compareArrays = (arr1, arr2) => {
  return JSON.stringify(arr1) === JSON.stringify(arr2);
};

function BusinessRules() {
  const { classes } = useStyles();
  const { Workspace_id } = useParams();
  const navigate = useNavigate();
  const workspace = useSelector((state) => state.workspace);
  const workspaceName = workspace.workspace.Name;

  const location = useLocation();
  const prevPathText = useMemo(() => location?.state?.source || "data-sources", [location?.state?.source]);

  const [loading, setLoading] = useState(false);
  const [searchOptions, setSearchOptions] = useState({
    sourcesAndEntities: [],
    searchValue: "",
    selectedSourcesAndEntities: [],
    appliedComponents: [],
  });
  const [paginationOption, setPaginationOption] = useState({
    totalCount: 0,
    currentPage: 0,
    limit: 10,
  });
  const [rules, setRules] = useState("");

  const CustomPaper = (props) => {
    return (
      <Paper {...props}>
        <Box
          sx={{
            "& .MuiAutocomplete-listbox": {
              maxWidth: "100%",
              wordBreak: "break-all !important",
            },
            "& .MuiAutocomplete-noOptions": {
              textAlign: "center",
            },
          }}
        >
          <>{props.children}</>
        </Box>
        <Box display="flex" flexDirection={"row"} justifyContent="space-between">
          <IconButton
            sx={{
              margin: "7px  10px 0px 10px",
              color: searchOptions.appliedComponents.length === 0 ? "rgba(0, 0, 0, 0.12)" : "#101010",
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              setPrevState(setSearchOptions, { selectedSourcesAndEntities: [] });
              getBusinessRules([], 0, paginationOption.limit);
            }}
            disabled={searchOptions.appliedComponents.length === 0}
          >
            <FilterAltOffIcon size="small" />
          </IconButton>
          <ButtonUI
            variant="contained"
            color="success"
            sx={{ margin: "7px  10px 7px 0" }}
            onMouseDown={(e) => {
              e.preventDefault();
              handleApplyFilterClick();
            }}
            disabled={compareArrays(searchOptions.selectedSourcesAndEntities, searchOptions.appliedComponents)}
          >
            Apply
          </ButtonUI>
        </Box>
      </Paper>
    );
  };

  const handleChangePage = (_event, newPage) => {
    setPrevState(setPaginationOption, { currentPage: newPage });
    getBusinessRules(searchOptions.selectedSourcesAndEntities, newPage, paginationOption.limit);
  };

  const handleApplyFilterClick = () => {
    if (!compareArrays(searchOptions.selectedSourcesAndEntities, searchOptions.appliedComponents)) {
      setPrevState(setPaginationOption, { currentPage: 0 });
      setPrevState(setPaginationOption, { appliedComponents: searchOptions.selectedSourcesAndEntities });
      getBusinessRules(searchOptions.selectedSourcesAndEntities, 0, paginationOption.limit);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setPrevState(setPaginationOption, { limit: parseInt(event.target.value, 10), currentPage: 0 });
    getBusinessRules(searchOptions.selectedSourcesAndEntities, 0, parseInt(event.target.value, 10));
  };

  const getBusinessRules = useCallback(
    async (selectedSourcesAndEntities, page, limit) => {
      setLoading(true);
      const bRulesService = new BusinessRulesService();
      const data = await bRulesService.getRules(Workspace_id, {
        page,
        limit,
        components: selectedSourcesAndEntities,
      });
      if (data.success) {
        setRules(data.data.rules);
        setPrevState(setPaginationOption, { totalCount: data.data.count });
      }
      setLoading(false);
    },
    [Workspace_id]
  );

  useEffect(() => {
    const getInitialData = async () => {
      setLoading(true);
      const bRulesService = new BusinessRulesService();
      const rules = await bRulesService.getRules(Workspace_id, {
        page: 0,
        limit: 10,
        components: [],
      });
      if (rules.success) {
        setRules(rules.data.rules);
        setPrevState(setPaginationOption, { totalCount: rules.data.count });
      }
      const components = await bRulesService.getComponents(Workspace_id);
      if (components.success) {
        setPrevState(setSearchOptions, { sourcesAndEntities: components.data.components });
      }

      setLoading(false);
    };
    if (Workspace_id) {
      getInitialData();
    }
  }, [workspaceName, Workspace_id, navigate, setSearchOptions]);

  const breadcrumbArr = useMemo(
    () => [
      { link: "/my-workspaces", displayName: "Workspaces", active: false },
      { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
      {
        link: `/my-workspaces/${Workspace_id}/${prevPathText}`,
        displayName: prevPathText === "data-sources" ? DATA_SOURCE.plural : "Entities",
        active: false,
      },
      { link: "#", displayName: "Business Rules", active: true },
    ],
    [Workspace_id, prevPathText, workspaceName]
  );

  return (
    <CommonLayout title="Data Modeling" breadcrumbArr={breadcrumbArr}>
      <Container maxWidth="false" sx={{ mt: 0, mb: 4, mx: 0 }}>
        <Box>
          <AppBar
            sx={{
              position: "relative",
              boxShadow: 0,
              backgroundColor: "#e8edf1",
            }}
          >
            <Box className={classes.Header}>
              <Toolbar
                sx={{
                  paddingLeft: "0 !important",
                }}
              >
                <Typography className={classes.HeaderTitle} component="div">
                  Business Rules
                </Typography>
              </Toolbar>
              <Grid container spacing={2} sx={{ width: "100%", mb: "15px" }}>
                <Grid
                  sx={{ maxWidth: "400px !important" }}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4
                  }}>
                  <Autocomplete
                    classes={{
                      option: classes.option,
                    }}
                    className={classes.AutoComplete}
                    disabled={loading}
                    disableCloseOnSelect
                    disableClearable
                    fullWidth
                    multiple
                    inputValue={searchOptions.searchValue}
                    isOptionEqualToValue={(option, value) => {
                      return option.Name === value.Name && option.Type === value.Type;
                    }}
                    noOptionsText={"No matches"}
                    options={searchOptions.sourcesAndEntities}
                    PaperComponent={(props) => <CustomPaper {...props} />}
                    PopperComponent={(props) => (
                      <Popper
                        {...props}
                        style={{
                          ...props.style,
                          zIndex: 1100,
                        }}
                      />
                    )}
                    size="small"
                    sx={{ maxWidth: "400px" }}
                    value={searchOptions.selectedSourcesAndEntities}
                    getOptionLabel={(option) => option.Name}
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={`${option.Name}_${option.Type}`}>
                          <ComponentAvatar componentType={option.Type} />
                          {option.Name}
                        </li>
                      );
                    }}
                    onBlur={(e) => {
                      if (!compareArrays(searchOptions.selectedSourcesAndEntities, searchOptions.appliedComponents)) {
                        setPrevState(setSearchOptions, {
                          selectedSourcesAndEntities: searchOptions.appliedComponents,
                        });
                      }
                    }}
                    onChange={(e, selectedObj, reason) => {
                      if (reason !== "clear") {
                        setPrevState(setSearchOptions, {
                          selectedSourcesAndEntities: selectedObj,
                        });
                      }
                    }}
                    onFocus={() => {
                      setPrevState(setSearchOptions, {
                        appliedComponents: searchOptions.selectedSourcesAndEntities,
                      });
                    }}
                    onInputChange={(e, value) => {
                      setPrevState(setSearchOptions, { searchValue: value });
                    }}
                    renderTags={(selected) => {
                      return selected.length <= 2 ? (
                        <>
                          {selected.map((selectedChip) => (
                            <Chip
                              label={selectedChip.Name}
                              size="small"
                              className={classes.SelectChip}
                              key={`${selectedChip.Name}_${selectedChip.Type}`}
                            />
                          ))}
                        </>
                      ) : (
                        <>
                          <>
                            {selected.slice(0, 2).map((selectedChip) => (
                              <Chip
                                label={selectedChip.Name}
                                size="small"
                                className={classes.SelectChip}
                                key={`${selectedChip.Name}_${selectedChip.Type}`}
                              />
                            ))}
                          </>
                          <p
                            style={{
                              fontSize: "0.8rem",
                              margin: "4px 0 0 2px",
                            }}
                          >{`+ ${selected.length - 2}`}</p>
                        </>
                      );
                    }}
                    renderInput={(params) => {
                      return (
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Component"
                          {...params}
                          InputProps={{
                            ...params.InputProps,
                            ...(searchOptions.searchValue && {
                              endAdornment: (
                                <InputAdornment
                                  sx={{
                                    marginRight: "-32px",
                                  }}
                                  position="end"
                                >
                                  <IconButton
                                    disabled={!searchOptions.searchValue}
                                    onClick={(e) => {
                                      setPrevState(setSearchOptions, { searchValue: "" });
                                    }}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }),
                          }}
                        />
                      );
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </AppBar>
          <DataTable
            rules={rules}
            loading={loading}
            count={paginationOption.totalCount}
            limit={paginationOption.limit}
            currentPage={paginationOption.currentPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Box>
      </Container>
    </CommonLayout>
  );
}

export default BusinessRules;
